import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, RefreshCw, AlertTriangle, CheckCircle, Clock, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { useDriversStore } from '@/store/driversStore';
import { useVehiclesStore } from '@/store/vehiclesStore';
import { apiUtils } from '@/services/api';
import DriverFormDialog from './DriverFormDialog';
import DriverDetailsDialog from './DriverDetailsDialog';
import DriverFilters from './DriverFilters';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const DriverManagement = ({ transporterId, onStatisticsUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);

  const {
    drivers,
    driversLoading,
    driversError,
    driverStatistics,
    filters,
    loadDriversByTransporter,
    createDriver,
    updateDriver,
    deleteDriver,
    assignVehicle,
    unassignVehicle,
    updateFilters,
    clearErrors,
    getFormattedDrivers,
  } = useDriversStore();

  const {
    vehicles,
    vehiclesLoading,
    loadVehiclesByTransporter,
  } = useVehiclesStore();

  // Charger les données au montage
  useEffect(() => {
    if (transporterId) {
      loadInitialData();
    }
  }, [transporterId]);

  // Charger les données initiales
  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadDriversByTransporter(transporterId),
        loadVehiclesByTransporter(transporterId)
      ]);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données des chauffeurs et véhicules',
        variant: 'destructive',
      });
    }
  };

  // Gestion de la recherche
  const handleSearch = (value) => {
    setSearchTerm(value);
    updateFilters({ search: value });
  };

  // Gestion des filtres
  const handleFiltersChange = (newFilters) => {
    updateFilters(newFilters);
  };

  // Rafraîchir les données
  const handleRefresh = async () => {
    try {
      await loadDriversByTransporter(transporterId, filters);
      toast({
        title: 'Succès',
        description: 'Données mises à jour',
      });
    } catch (error) {
      console.error('Erreur lors du rafraîchissement:', error);
    }
  };

  // Ouvrir le formulaire d'ajout
  const handleAddDriver = () => {
    setEditingDriver(null);
    setIsFormOpen(true);
  };

  // Ouvrir le formulaire d'édition
  const handleEditDriver = (driver) => {
    setEditingDriver(driver);
    setIsFormOpen(true);
  };

  // Ouvrir les détails
  const handleViewDriver = (driver) => {
    setSelectedDriver(driver);
    setIsDetailsOpen(true);
  };

  // Soumettre le formulaire
  const handleSubmit = async (driverData) => {
    try {
      const dataWithTransporter = {
        ...driverData,
        transporter: transporterId,
      };

      if (editingDriver) {
        await updateDriver(editingDriver.id, dataWithTransporter);
        toast({
          title: 'Succès',
          description: 'Chauffeur modifié avec succès',
        });
      } else {
        await createDriver(dataWithTransporter);
        toast({
          title: 'Succès',
          description: 'Chauffeur ajouté avec succès',
        });
      }

      setIsFormOpen(false);
      setEditingDriver(null);
      
      // Rafraîchir les données
      await loadDriversByTransporter(transporterId, filters);
      
      // Notifier le parent pour mettre à jour les statistiques
      if (onStatisticsUpdate) {
        onStatisticsUpdate();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      if (error.type === 'validation') {
        // Les erreurs de validation sont gérées par le formulaire
        throw error;
      } else {
        toast({
          title: 'Erreur',
          description: error.message || 'Impossible de sauvegarder le chauffeur',
          variant: 'destructive',
        });
      }
    }
  };

  // Supprimer un chauffeur
  const handleDeleteDriver = async (driver) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le chauffeur ${driver.first_name} ${driver.last_name} ?`)) {
      return;
    }

    try {
      await deleteDriver(driver.id);
      toast({
        title: 'Succès',
        description: 'Chauffeur supprimé avec succès',
      });
      
      // Rafraîchir les données
      await loadDriversByTransporter(transporterId, filters);
      
      // Notifier le parent pour mettre à jour les statistiques
      if (onStatisticsUpdate) {
        onStatisticsUpdate();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de supprimer le chauffeur',
        variant: 'destructive',
      });
    }
  };

  // Assigner un véhicule
  const handleAssignVehicle = async (driverId, vehicleId) => {
    try {
      await assignVehicle(driverId, vehicleId);
      toast({
        title: 'Succès',
        description: 'Véhicule assigné avec succès',
      });
      
      // Rafraîchir les données
      await loadDriversByTransporter(transporterId, filters);
    } catch (error) {
      console.error('Erreur lors de l\'assignation:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'assigner le véhicule',
        variant: 'destructive',
      });
    }
  };

  // Désassigner un véhicule
  const handleUnassignVehicle = async (driverId) => {
    try {
      await unassignVehicle(driverId);
      toast({
        title: 'Succès',
        description: 'Véhicule désassigné avec succès',
      });
      
      // Rafraîchir les données
      await loadDriversByTransporter(transporterId, filters);
    } catch (error) {
      console.error('Erreur lors de la désassignation:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de désassigner le véhicule',
        variant: 'destructive',
      });
    }
  };

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    const colors = {
      'ACTIVE': 'bg-green-500/20 text-green-400 border-green-500/30',
      'INACTIVE': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      'PENDING': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // Obtenir la couleur du statut du permis
  const getLicenseColor = (status) => {
    const colors = {
      'valid': 'text-green-500',
      'expiring_soon': 'text-orange-500',
      'expired': 'text-red-500',
      'unknown': 'text-gray-500',
    };
    return colors[status] || 'text-gray-500';
  };

  // Obtenir l'icône du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <CheckCircle className="w-4 h-4" />;
      case 'expiring_soon':
        return <Clock className="w-4 h-4" />;
      case 'expired':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const formattedDrivers = getFormattedDrivers();

  return (
    <div className="space-y-6">
      {/* En-tête avec actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Gestion des Chauffeurs</h3>
          <p className="text-sm text-muted-foreground">
            {driverStatistics?.total || 0} chauffeur(s) enregistré(s)
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            onClick={handleAddDriver}
            className="bg-green-600 hover:bg-green-700"
            disabled={driversLoading}
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un chauffeur
          </Button>
          
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={driversLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${driversLoading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Rechercher par nom, téléphone, permis..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="w-full sm:w-auto"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filtres
        </Button>
      </div>

      {/* Filtres avancés */}
      {showFilters && (
        <DriverFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Statistiques rapides */}
      {driverStatistics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-muted-foreground">Actifs</span>
              </div>
              <p className="text-2xl font-bold">{driverStatistics.by_status?.ACTIVE || 0}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">Assignés</span>
              </div>
              <p className="text-2xl font-bold">{driverStatistics.assigned || 0}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-muted-foreground">Non assignés</span>
              </div>
              <p className="text-2xl font-bold">{driverStatistics.unassigned || 0}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-muted-foreground">Permis expiré</span>
              </div>
              <p className="text-2xl font-bold">{driverStatistics.license_expired || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Liste des chauffeurs */}
      {driversLoading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Chargement des chauffeurs...</span>
        </div>
      ) : driversError ? (
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-500 mb-2">Erreur lors du chargement</p>
            <p className="text-sm text-muted-foreground mb-4">{driversError.message}</p>
            <Button onClick={handleRefresh} variant="outline">
              Réessayer
            </Button>
          </CardContent>
        </Card>
      ) : formattedDrivers.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Aucun chauffeur</h3>
            <p className="text-muted-foreground mb-4">
              Commencez par ajouter votre premier chauffeur
            </p>
            <Button onClick={handleAddDriver} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un chauffeur
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {formattedDrivers.map((driver) => (
            <Card key={driver.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h4 className="font-semibold text-lg">{driver.full_name}</h4>
                      {driver.logiconnect_id && (
                        <LogiConnectIdBadge 
                          id={driver.logiconnect_id} 
                          showLabel={false}
                          size="sm"
                        />
                      )}
                      <Badge className={getStatusColor(driver.status)}>
                        {driver.status_display}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Téléphone:</span>
                        <p className="font-medium">{driver.phone}</p>
                      </div>
                      
                      <div>
                        <span className="text-muted-foreground">Permis:</span>
                        <p className="font-medium">{driver.license_number}</p>
                      </div>
                      
                      <div>
                        <span className="text-muted-foreground">Validité permis:</span>
                        <div className={`flex items-center gap-1 ${getLicenseColor(driver.license_status)}`}>
                          {getStatusIcon(driver.license_status)}
                          <span className="text-xs">
                            {driver.license_valid_until 
                              ? new Date(driver.license_valid_until).toLocaleDateString('fr-FR')
                              : 'Non renseignée'
                            }
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-muted-foreground">Véhicule assigné:</span>
                        <p className="font-medium">
                          {driver.assigned_vehicle_plate || 'Aucun'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDriver(driver)}
                    >
                      Voir
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditDriver(driver)}
                    >
                      Modifier
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteDriver(driver)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Supprimer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <DriverFormDialog
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingDriver(null);
        }}
        onSubmit={handleSubmit}
        driver={editingDriver}
        transporterId={transporterId}
        vehicles={vehicles}
        isLoading={vehiclesLoading}
      />

      <DriverDetailsDialog
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedDriver(null);
        }}
        driver={selectedDriver}
        onEdit={handleEditDriver}
        onDelete={handleDeleteDriver}
        onAssignVehicle={handleAssignVehicle}
        onUnassignVehicle={handleUnassignVehicle}
      />
    </div>
  );
};

export default DriverManagement;